import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { PlanogrammCard } from '../../components/PlanogrammCard';
import { UploadPlanogrammModal } from '../../components/UploadPlanogrammModal';
import { EditPlanogrammModal } from '../../components/EditPlanogrammModal';
import { Plus, Search, LayoutGrid, List, FolderPlus, Trash2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlanogrammPage() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const canWrite = ['admin', 'stationsleitung', 'superadmin'].includes(user?.role);

  const [planogramme, setPlanogramme] = useState([]);
  const [bereiche, setBereiche] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBereich, setFilterBereich] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  // Modal
  const [showUpload, setShowUpload] = useState(false);
  const [editingPlanogramm, setEditingPlanogramm] = useState(null);

  // Bereich management
  const [newBereichName, setNewBereichName] = useState('');
  const [showBereichInput, setShowBereichInput] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, bRes, lRes] = await Promise.all([
        axios.get(`${API}/api/planogramm/`, { headers }),
        axios.get(`${API}/api/planogramm/bereiche`, { headers }),
        axios.get(`${API}/api/user/locations`, { headers }),
      ]);
      setPlanogramme(pRes.data || []);
      setBereiche(bRes.data || []);
      setLocations(lRes.data || []);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
      // Mark planogramm notifications as seen
      axios.post(`${API}/api/planogramm/mark-seen`, {}, { headers }).catch(() => {});
    }
  }, [fetchData, token]);

  const handleCreateBereich = async () => {
    const name = newBereichName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/api/planogramm/bereiche`, { name }, { headers });
      toast.success(`Bereich "${name}" erstellt`);
      setNewBereichName('');
      setShowBereichInput(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleDeleteBereich = async (bereichId, bereichName) => {
    if (!window.confirm(`Bereich "${bereichName}" wirklich loschen?`)) return;
    try {
      await axios.delete(`${API}/api/planogramm/bereiche/${bereichId}`, { headers });
      toast.success(`Bereich "${bereichName}" geloscht`);
      if (filterBereich === bereichId) setFilterBereich('all');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Loschen');
    }
  };

  const handleDeletePlanogramm = async (planogrammId) => {
    if (!window.confirm('Planogramm wirklich loschen?')) return;
    try {
      await axios.delete(`${API}/api/planogramm/${planogrammId}`, { headers });
      toast.success('Planogramm geloscht');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Loschen');
    }
  };

  const handleUploadSuccess = () => {
    setShowUpload(false);
    fetchData();
  };

  const handleEditSuccess = () => {
    setEditingPlanogramm(null);
    fetchData();
  };

  // Filter logic
  const filtered = planogramme.filter(p => {
    const matchesSearch =
      !searchTerm ||
      p.regal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.titel?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBereich =
      filterBereich === 'all' ||
      (filterBereich === 'none' ? !p.bereich_id : p.bereich_id === filterBereich);
    return matchesSearch && matchesBereich;
  });

  // Map bereich_id to name
  const bereichMap = {};
  bereiche.forEach(b => { bereichMap[b.id] = b.name; });

  // Map location_id to name
  const locationMap = {};
  locations.forEach(l => { locationMap[l.id] = l.name; });

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 md:p-6" data-testid="planogramm-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="planogramm-title">Planogramme</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Platzierungsanleitungen und Regalplatzierungen
            </p>
          </div>
          {canWrite && (
            <Button
              onClick={() => setShowUpload(true)}
              className="gap-2"
              data-testid="upload-planogramm-btn"
            >
              <Plus className="h-4 w-4" />
              Planogramm hochladen
            </Button>
          )}
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Suche nach Regal oder Titel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="planogramm-search"
            />
          </div>
          <Select value={filterBereich} onValueChange={setFilterBereich}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="planogramm-filter-bereich">
              <SelectValue placeholder="Alle Bereiche" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Bereiche</SelectItem>
              <SelectItem value="none">Ohne Bereich</SelectItem>
              {bereiche.map(b => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1 border rounded-md p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={() => setViewMode('grid')}
              data-testid="view-mode-grid"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={() => setViewMode('list')}
              data-testid="view-mode-list"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Bereich Management (Admin only) */}
        {canWrite && (
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Bereiche verwalten</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBereichInput(!showBereichInput)}
                className="gap-1"
                data-testid="add-bereich-btn"
              >
                <FolderPlus className="h-3.5 w-3.5" />
                Neuer Bereich
              </Button>
            </div>
            {showBereichInput && (
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Bereichsname..."
                  value={newBereichName}
                  onChange={(e) => setNewBereichName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateBereich()}
                  className="max-w-xs"
                  data-testid="new-bereich-input"
                />
                <Button size="sm" onClick={handleCreateBereich} data-testid="save-bereich-btn">
                  Erstellen
                </Button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {bereiche.length === 0 && (
                <p className="text-sm text-muted-foreground">Noch keine Bereiche angelegt.</p>
              )}
              {bereiche.map(b => (
                <Badge key={b.id} variant="secondary" className="gap-1 pr-1" data-testid={`bereich-badge-${b.id}`}>
                  {b.name}
                  <button
                    onClick={() => handleDeleteBereich(b.id, b.name)}
                    className="ml-1 hover:text-destructive"
                    data-testid={`delete-bereich-${b.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="planogramm-empty">
            <p className="text-lg font-medium">Keine Planogramme gefunden</p>
            <p className="text-sm mt-1">
              {searchTerm || filterBereich !== 'all'
                ? 'Versuchen Sie andere Suchkriterien.'
                : canWrite
                  ? 'Laden Sie das erste Planogramm hoch.'
                  : 'Ihr Admin hat noch keine Planogramme bereitgestellt.'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="planogramm-grid">
            {filtered.map(p => (
              <PlanogrammCard
                key={p.id}
                planogramm={p}
                bereichName={bereichMap[p.bereich_id]}
                locationMap={locationMap}
                canDelete={canWrite}
                onDelete={handleDeletePlanogramm}
                onEdit={(pl) => setEditingPlanogramm(pl)}
                token={token}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2" data-testid="planogramm-list">
            {filtered.map(p => (
              <PlanogrammCard
                key={p.id}
                planogramm={p}
                bereichName={bereichMap[p.bereich_id]}
                locationMap={locationMap}
                canDelete={canWrite}
                onDelete={handleDeletePlanogramm}
                onEdit={(pl) => setEditingPlanogramm(pl)}
                token={token}
                listMode
              />
            ))}
          </div>
        )}

        {/* Upload Modal */}
        {showUpload && (
          <UploadPlanogrammModal
            open={showUpload}
            onClose={() => setShowUpload(false)}
            onSuccess={handleUploadSuccess}
            bereiche={bereiche}
            locations={locations}
            token={token}
          />
        )}

        {/* Edit Modal */}
        {editingPlanogramm && (
          <EditPlanogrammModal
            open={!!editingPlanogramm}
            onClose={() => setEditingPlanogramm(null)}
            onSuccess={handleEditSuccess}
            planogramm={editingPlanogramm}
            bereiche={bereiche}
            locations={locations}
            token={token}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
